import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface MockLocation {
  lat: number;
  lng: number;
  /** Optional human label (e.g. the geocoded place name). */
  label?: string;
}

interface MockLocationContextValue {
  /** Active test override, or null when using the real device location. */
  mock: MockLocation | null;
  setMock: (mock: MockLocation | null) => void;
}

const MockLocationContext = createContext<MockLocationContextValue | null>(null);

const STORAGE_KEY = "triptracker.mockLocation";

function readStored(): MockLocation | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<MockLocation>;
    if (typeof parsed?.lat === "number" && typeof parsed?.lng === "number") {
      return {
        lat: parsed.lat,
        lng: parsed.lng,
        ...(typeof parsed.label === "string" ? { label: parsed.label } : {}),
      };
    }
  } catch {
    /* ignore unavailable / corrupt storage */
  }
  return null;
}

/**
 * Holds an optional device-location override used to exercise the
 * location-dependent features (nearby search, GPS capture, Journey) without
 * physically being there. Persisted per-device in localStorage; set from the
 * Admin → Mock Location card.
 *
 * `LocationTrackingContext` also mirrors it to `userLocations/{uid}/config` in
 * RTDB, where the watch picks it up — that client has no override UI of its
 * own and can't easily get one, since a Wear emulator never gets past the
 * "waiting for phone" sign-in.
 */
export function MockLocationProvider({ children }: { children: ReactNode }) {
  const [mock, setMockState] = useState<MockLocation | null>(readStored);

  const setMock = useCallback((next: MockLocation | null) => {
    setMockState(next);
    try {
      if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore unavailable storage */
    }
  }, []);

  // Keep multiple tabs in sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setMockState(readStored());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(() => ({ mock, setMock }), [mock, setMock]);

  return <MockLocationContext.Provider value={value}>{children}</MockLocationContext.Provider>;
}

export function useMockLocation(): MockLocationContextValue {
  const ctx = useContext(MockLocationContext);
  if (!ctx) {
    throw new Error("useMockLocation must be used within a MockLocationProvider");
  }
  return ctx;
}
